import { Flex, Text, Box, Tooltip } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEdit,
  faExclamationCircle,
  faTrashAlt,
  faUserPlus
} from '@fortawesome/pro-regular-svg-icons';
import { Badge, ButtonText, Checkbox } from '@morressier/alembic';
import Divider from '../../../Divider';
import { getRScoreBadgeVariant } from '../../../../utils/badgeVariants';
import { useReviewerInviteList } from '../../../../contexts/ReviewerInviteListProvider';
import { useReviewerSelection } from '../../../../contexts/ReviewerSelectionContext';
import { getEmailFromReviewer } from '../../../../utils/reviews';
import { OverwriteReviewsModal } from '../../AdditionalInformation/editorial/common/OverwriteReviewsModal';
import { useState, useEffect, useRef } from 'react';
import { useSubmissionData } from '../../../../contexts/SubmissionDataProvider';
import { useMutation } from '@tanstack/react-query';
import { checkUser, fetchAuthorProfileData } from '../../../../api/queries';
import AddUser from '../../../User/AddUser/AddUser';
import { useSubmissionContextData } from '../../../../contexts/SubmissionContextProvider';
import { ILexiconField } from '@publishing-engine/domain/models';

const getMeaningfulValue = (...values: Array<string | null | undefined>) => {
  const value = values.find(
    item =>
      item !== undefined && item !== null && item.trim() !== '' && item !== '-'
  );

  return value ?? '-';
};

const getActionCardConfig = (
  reviewerConfig: ILexiconField[] | undefined,
  predicateLabel: string
) =>
  reviewerConfig?.find(
    field =>
      field.predicateLabel === predicateLabel && field.actionCard === true
  );

const DeactivatedUserWarning = () => (
  <Tooltip
    hasArrow
    borderRadius={4}
    placement="left"
    shouldWrapChildren
    label="This account has been deactivated"
  >
    <Box
      as="span"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      width={8}
      height={8}
      color="actionSecondaryCautionContent"
    >
      <FontAwesomeIcon icon={faExclamationCircle} />
    </Box>
  </Tooltip>
);

const ReviewerActionCardDetails = ({
  reviewerConfig,
  institute,
  email,
  avgRScore,
  activeReviews,
  showInstitute = true,
  showEmail = true,
  showAvgRScore = true,
  showActiveReviews = true
}: {
  reviewerConfig?: ILexiconField[];
  institute: string;
  email: string;
  avgRScore: string;
  activeReviews: string;
  showInstitute?: boolean;
  showEmail?: boolean;
  showAvgRScore?: boolean;
  showActiveReviews?: boolean;
}) => {
  const institutionConfig = getActionCardConfig(
    reviewerConfig,
    'ReviewerInstitution'
  );
  const emailConfig = getActionCardConfig(
    reviewerConfig,
    'ReviewerPrimaryEmailAddress'
  );
  const avgRScoreConfig = getActionCardConfig(reviewerConfig, 'AverageRScore');
  const activeReviewsConfig = getActionCardConfig(
    reviewerConfig,
    'ActiveReviews'
  );

  return (
    <>
      {showInstitute && institutionConfig && (
        <Flex textStyle="textContext">
          <Text fontWeight="500">{institutionConfig.label}:</Text>
          <Text paddingLeft={2}>{institute}</Text>
        </Flex>
      )}

      {showEmail && emailConfig && (
        <Flex textStyle="textContext">
          <Text fontWeight="500">{emailConfig.label}:</Text>
          <Text paddingLeft={2}>{email}</Text>
        </Flex>
      )}

      {((showAvgRScore && avgRScoreConfig) ||
        (showActiveReviews && activeReviewsConfig)) && (
        <Flex textStyle="textContext" gap={4}>
          {showAvgRScore && avgRScoreConfig && (
            <Flex gap={1} alignItems="center">
              <Text fontWeight="500">{avgRScoreConfig.label}:</Text>
              {avgRScore !== '-' ? (
                (() => {
                  const { variant, label } = getRScoreBadgeVariant(avgRScore);
                  return (
                    <Badge variant={variant} size="context">
                      {label ? `${avgRScore} (${label})` : avgRScore}
                    </Badge>
                  );
                })()
              ) : (
                <Text paddingLeft={2}>{avgRScore}</Text>
              )}
            </Flex>
          )}

          {showActiveReviews && activeReviewsConfig && (
            <Flex gap={1}>
              <Text fontWeight="500">{activeReviewsConfig.label}:</Text>
              <Text paddingLeft={2}>{activeReviews}</Text>
            </Flex>
          )}
        </Flex>
      )}
    </>
  );
};

const ReviewersListSelectionAccountCreation = ({
  isAccountCreationEnabled
}: {
  isAccountCreationEnabled: boolean;
}) => {
  const {
    reviewerList,
    selectedReviewersToInvite,
    toggleSelectedReviewersToInvite,
    setSelectedReviewerToInvite,
    removeReviewersByEmails
  } = useReviewerInviteList();

  const { selectedReviewers, toggleReviewer } = useReviewerSelection();

  const [minReviewsModalOpen, setMinReviewsModalOpen] = useState(false);
  const [userCheckResults, setUserCheckResults] = useState<Record<string, any>>(
    {}
  );
  const checkedEmailsRef = useRef<Set<string>>(new Set());
  const deselectedByAccountCheckRef = useRef<Set<string>>(new Set());
  const [openAddUserModal, setAddUserModal] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<string>('');
  const { submissionContext } = useSubmissionContextData();
  const reviewerConfig = submissionContext?.config?.cms?.lexicon?.fields;

  const {
    refetchSubmission,
    submissionContextId,
    submissionId,
    organizationId,
    workflowResponse: { workflowData }
  } = useSubmissionData();

 const fetchAuthorProfileMutation = useMutation({
    mutationFn: ({ userId }: { userId: string; email: string }) =>
      fetchAuthorProfileData(organizationId, userId),
    onSuccess: (profileData, { email }) => {
      setUserCheckResults(prev => ({
        ...prev,
        [email]: {
          ...prev[email],
          profileData
        }
      }));
    },
    onError: (error, { email }) => {
      setUserCheckResults(prev => ({
        ...prev,
        [email]: {
          ...prev[email],
          profileError: error
        }
      }));
    }
  });

  const checkUserMutation = useMutation({
    mutationFn: (email: string) => checkUser(email),
    onSuccess: (data, email) => {
      setUserCheckResults(prev => ({
        ...prev,
        [email]: { success: true, data }
      }));
      if (data?.id) {
        fetchAuthorProfileMutation.mutate({ userId: data.id, email });
      }

      const isSelectableUser = Boolean(
        data?.id && (!data?.status || data.status === 'active')
      );

      if (isAccountCreationEnabled && !isSelectableUser) {
        // Deterministically deselect to avoid re-checking on remount toggling back to selected
        deselectedByAccountCheckRef.current.add(email);
        setSelectedReviewerToInvite(email, false);
      }
    },
    onError: (error, email) => {
      setUserCheckResults(prev => ({
        ...prev,
        [email]: { success: false, error }
      }));
      if (isAccountCreationEnabled) {
        // Account check failed: deselect so they cannot be invited without creating an account first
        deselectedByAccountCheckRef.current.add(email);
        setSelectedReviewerToInvite(email, false);
      }
    }
  });

  

  const pendingReviewers = reviewerList.filter(r => !r.invited);
  const invitedReviewers = reviewerList.filter(r => r.invited);
  const orderedReviewers = [...pendingReviewers, ...invitedReviewers];

  const isReviewerSelectable = (reviewer: (typeof reviewerList)[0]) => {
    const result = userCheckResults[reviewer.email];
    if (!isAccountCreationEnabled) return true;
    if (!result) return false;
    return Boolean(
      result.success &&
        result.data?.id &&
        (!result.data?.status || result.data.status === 'active')
    );
  };

  // Only include selectable reviewers for select all logic
  const selectablePendingReviewers =
    pendingReviewers.filter(isReviewerSelectable);

  const confirmedReviewers = reviewerList.filter(
    r => selectedReviewersToInvite[r.email] && !r.invited
  );

  const allSelected =
    selectablePendingReviewers.length > 0 &&
    selectablePendingReviewers.every(r => selectedReviewersToInvite[r.email]);

  const handleSelectAll = () => {
    const shouldSelect = !allSelected;
    selectablePendingReviewers.forEach(r => {
      if (selectedReviewersToInvite[r.email] !== shouldSelect) {
        toggleSelectedReviewersToInvite(r.email);
      }
    });
  };

  const unselectReviewersByEmail = (emails: string[]) => {
    selectedReviewers.forEach(r => {
      if (emails.includes(getEmailFromReviewer(r))) {
        toggleReviewer(r);
      }
    });
  };

  const deleteReviewer = (email: string) => {
    removeReviewersByEmails([email]);
    unselectReviewersByEmail([email]);

    // Clear cached data to allow re-checking when added again
    checkedEmailsRef.current.delete(email);
    deselectedByAccountCheckRef.current.delete(email);
    setUserCheckResults(prev => {
      const next = { ...prev };
      delete next[email];
      return next;
    });
  };

  const handleUserCreated = (userId: string, email: string) => {
    // Mark as checked to prevent duplicate checks
    checkedEmailsRef.current.add(email);

    // Set initial state with success and userId
    setUserCheckResults(prev => ({
      ...prev,
      [email]: { success: true, data: { id: userId } }
    }));

    // Fetch the full author profile data
    fetchAuthorProfileMutation.mutate({ userId, email });

    // Re-select the reviewer now that an account has been created
    // (they were auto-deselected when no account was found)
    if (deselectedByAccountCheckRef.current.has(email)) {
      deselectedByAccountCheckRef.current.delete(email);
      setSelectedReviewerToInvite(email, true);
    }
  };

  const handleDeleteSelected = () => {
    const emailsToDelete = pendingReviewers
      .filter(r => selectedReviewersToInvite[r.email])
      .map(r => r.email);

    if (!emailsToDelete.length) return;

    removeReviewersByEmails(emailsToDelete);
    unselectReviewersByEmail(emailsToDelete);

    // Clear cached data for all deleted emails
    emailsToDelete.forEach(email => {
      checkedEmailsRef.current.delete(email);
      deselectedByAccountCheckRef.current.delete(email);
    });
    setUserCheckResults(prev => {
      const next = { ...prev };
      emailsToDelete.forEach(email => {
        delete next[email];
      });
      return next;
    });
  };

  useEffect(() => {
    const pendingReviewersToCheck = reviewerList.filter(
      r => !r.invited && !checkedEmailsRef.current.has(r.email)
    );

    pendingReviewersToCheck.forEach(reviewer => {
      // Mark as checked before mutating to prevent duplicate calls
      checkedEmailsRef.current.add(reviewer.email);
      checkUserMutation.mutate(reviewer.email);
    });
  }, [reviewerList]);

  return (
    <Flex
      flexDirection="column"
      gap={2}
      padding={4}
      backgroundColor="surfacePrimaryFill"
      position="sticky"
      top="60px"
      maxH="calc(100vh - 60px)"
    >
      {/* Header */}
      <Flex justifyContent="space-between">
        <Flex alignItems="center" gap={2}>
          <Checkbox
            name="deleteAll"
            id="deleteAll"
            ariaLabel="deleteAllReviewers"
            isChecked={allSelected}
            disabled={!selectablePendingReviewers.length}
            onChange={handleSelectAll}
          >
            {' '}
          </Checkbox>
          <Text textStyle="headingItem">
            Added Reviewers ({reviewerList.length})
          </Text>
        </Flex>

        <ButtonText
          variant="caution"
          disabled={!confirmedReviewers.length}
          onClick={handleDeleteSelected}
        >
          <FontAwesomeIcon icon={faTrashAlt} />
        </ButtonText>
      </Flex>

      {Divider}

      <Flex alignItems="center">
        <FontAwesomeIcon icon={faExclamationCircle} color="blue" />
        <Text textStyle="textContext" fontWeight="500" paddingLeft={1}>
          Target reviewers: {workflowData.minReviews}
        </Text>
        <ButtonText onClick={() => setMinReviewsModalOpen(true)}>
          <FontAwesomeIcon icon={faEdit} />
        </ButtonText>
      </Flex>

      {/* Reviewers */}
      {orderedReviewers.length ? (
        <Flex flexDirection="column" width="100%" overflowY="auto" flex="1">
          <Box>
            {orderedReviewers.map((field, index) => {
              const userCheckResult = userCheckResults[field.email];
              const profileUser = userCheckResult?.profileData?.user;
              const hasExistingAccount = Boolean(
                userCheckResult?.success && userCheckResult?.data?.id
              );
              const hasLoadedProfile = Boolean(profileUser);
              const shouldShowCreateAccountButton = Boolean(
                userCheckResult && !userCheckResult.data?.id
              );
              const isUserDeactivated =
                !!userCheckResult && userCheckResult?.data?.status === 'deactivated';
              const displayName = getMeaningfulValue(
                field.meta?.name,
                [profileUser?.firstName, profileUser?.lastName]
                  .filter(Boolean)
                  .join(' '),
                field.email.split('@')[0]
              );
              const institute = getMeaningfulValue(
                field.meta?.institute,
                profileUser?.affiliationDetails?.organizationName
              );
              const avgRScore = getMeaningfulValue(field.meta?.avgRScore);
              const activeReviews = getMeaningfulValue(
                field.meta?.activeReviews
              );

              return (
                <Flex key={index}>
                  <Flex
                    paddingY={2}
                    gap={4}
                    flexDirection="column"
                    borderRadius="md"
                    width="100%"
                  >
                    <Flex flexDirection="column">
                      {field.source === 'search' ? (
                        <>
                          <Flex mb={2} justifyContent="space-between">
                            <Checkbox
                              id={`select-reviewer-${index}`}
                              name={`select-reviewer-${index}`}
                              ariaLabel={`select-reviewer-${index}`}
                              isChecked={selectedReviewersToInvite[field.email]}
                              onChange={() =>
                                toggleSelectedReviewersToInvite(field.email)
                              }
                              disabled={
                                field.invited ||
                                isUserDeactivated ||
                                !isReviewerSelectable(field)
                              }
                            >
                              <Text textStyle="headingItem">{displayName}</Text>
                            </Checkbox>
                            <Flex gap={1}>
                              {shouldShowCreateAccountButton && (
                                <ButtonText
                                  variant="primary"
                                  onClick={() => {
                                    setSelectedUser(field.email);
                                    setAddUserModal(true);
                                  }}
                                  disabled={field.invited}
                                >
                                  <FontAwesomeIcon icon={faUserPlus} />
                                </ButtonText>
                              )}
                              {isUserDeactivated && <DeactivatedUserWarning />}
                              <ButtonText
                                variant="caution"
                                onClick={() => deleteReviewer(field.email)}
                                disabled={field.invited}
                              >
                                <FontAwesomeIcon icon={faTrashAlt} />
                              </ButtonText>
                            </Flex>
                          </Flex>
                          <ReviewerActionCardDetails
                            reviewerConfig={reviewerConfig}
                            institute={institute}
                            email={field.email}
                            avgRScore={avgRScore}
                            activeReviews={activeReviews}
                          />
                        </>
                      ) : (
                        <Flex flexDirection="column" marginTop={2}>
                          {hasExistingAccount && hasLoadedProfile ? (
                            <>
                              <Flex justifyContent="space-between">
                                <Checkbox
                                  id={`select-reviewer-${index}`}
                                  name={`select-reviewer-${index}`}
                                  ariaLabel={`select-reviewer-${index}`}
                                  isChecked={
                                    selectedReviewersToInvite[field.email]
                                  }
                                  onChange={() =>
                                    toggleSelectedReviewersToInvite(field.email)
                                  }
                                  disabled={field.invited || isUserDeactivated}
                                >
                                  <Text textStyle="headingItem">
                                    {displayName}
                                  </Text>
                                </Checkbox>

                                <Flex gap={1}>
                                  {isUserDeactivated && (
                                    <DeactivatedUserWarning />
                                  )}
                                  <ButtonText
                                    variant="caution"
                                    onClick={() => deleteReviewer(field.email)}
                                    disabled={field.invited}
                                  >
                                    <FontAwesomeIcon icon={faTrashAlt} />
                                  </ButtonText>
                                </Flex>
                              </Flex>

                              <ReviewerActionCardDetails
                                reviewerConfig={reviewerConfig}
                                institute={institute}
                                email={field.email}
                                avgRScore={avgRScore}
                                activeReviews={activeReviews}
                                showAvgRScore={false}
                                showActiveReviews={false}
                              />
                            </>
                          ) : (
                            <>
                              <Flex justifyContent="space-between">
                                <Checkbox
                                  id={`select-reviewer-${index}`}
                                  name={`select-reviewer-${index}`}
                                  ariaLabel={`select-reviewer-${index}`}
                                  isChecked={
                                    selectedReviewersToInvite[field.email]
                                  }
                                  onChange={() =>
                                    toggleSelectedReviewersToInvite(field.email)
                                  }
                                  disabled={
                                    isAccountCreationEnabled ||
                                    field.invited ||
                                    isUserDeactivated
                                  }
                                >
                                  <Text textStyle="headingItem">
                                    {field.email.split('@')[0]}
                                  </Text>
                                </Checkbox>
                                <Flex gap={1}>
                                  {shouldShowCreateAccountButton && (
                                    <ButtonText
                                      variant="primary"
                                      onClick={() => {
                                        setSelectedUser(field.email);
                                        setAddUserModal(true);
                                      }}
                                      disabled={field.invited}
                                    >
                                      <FontAwesomeIcon icon={faUserPlus} />
                                    </ButtonText>
                                  )}
                                  {isUserDeactivated && (
                                    <DeactivatedUserWarning />
                                  )}
                                  <ButtonText
                                    variant="caution"
                                    onClick={() => deleteReviewer(field.email)}
                                    disabled={field.invited}
                                  >
                                    <FontAwesomeIcon icon={faTrashAlt} />
                                  </ButtonText>
                                </Flex>
                              </Flex>
                              <Flex>
                                <Text fontWeight="500">Email:</Text>
                                <Text paddingLeft={2}>{field.email}</Text>
                              </Flex>
                            </>
                          )}
                        </Flex>
                      )}
                    </Flex>

                    {field.invited && (
                      <Badge variant="neutral">
                        Mail has been sent to the reviewer
                      </Badge>
                    )}

                    {index !== orderedReviewers.length - 1 && Divider}
                  </Flex>
                </Flex>
              );
            })}
          </Box>
          <AddUser
            title="Create Reviewer Account"
            submitButtonText="Create Account"
            isOpen={openAddUserModal}
            onClose={() => setAddUserModal(false)}
            organizationId={organizationId}
            workspaceId={submissionContext?.workspaceId}
            submissionContextId={submissionContextId}
            onSuccess={handleUserCreated}
            email={selectedUser}
          />
        </Flex>
      ) : (
        <Text textStyle="textContext">
          Here your added reviewers will appear. Select a reviewer from the left
          panel or click &apos;Add by Email&apos; to add more.
        </Text>
      )}

      {minReviewsModalOpen && (
        <OverwriteReviewsModal
          handleCloseModal={() => setMinReviewsModalOpen(false)}
          defaultMinReviews={workflowData?.minReviews}
          refetchSubmission={refetchSubmission}
          submissionContextId={submissionContextId}
          organizationId={organizationId}
          submissionId={submissionId}
        />
      )}
    </Flex>
  );
};

export default ReviewersListSelectionAccountCreation;
